import { useState, useEffect } from "react";
import { View, StyleSheet, Text } from "react-native";
import { platformAlert } from "../utils/platformAlert";
import { spacing } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";
import { supabase } from "../lib/supabase";
import { useAuth } from "../hooks/useAuth";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { SavedAddressList } from "../components/ui/SavedAddressList";
import { useDeliveryAddressContext } from "../contexts/DeliveryAddressContext";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import { SafeAreaView } from "react-native-safe-area-context";
import {
  formatPhoneNumberAsUserTypes,
  validatePhoneNumberWithError,
  formatPhoneNumberForAPI,
} from "../utils/phoneValidation";
import { useGeocoding } from "../hooks/useGeocoding";
import GeocodingService from "../services/GeocodingService";
import { Feather } from "@expo/vector-icons";
import { usePhoneVerification } from "../hooks/usePhoneVerification";
import { PhoneVerification } from "../components/ui/PhoneVerification";

interface SavedAddress {
  id: string;
  street_address: string;
  apartment?: string;
  city: string;
  state: string;
  borough?: string;
  zip_code: string;
  phone_number: string;
  delivery_instructions?: string;
  is_default: boolean;
  [key: string]: any;
}

export default function DeliveryAddressScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  const { user } = useAuth();
  const { updateAddress, selectedAddress } = useDeliveryAddressContext();
  const { geocodeAddress } = useGeocoding();
  const [loading, setLoading] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeResult, setGeocodeResult] = useState<{
    latitude: number;
    longitude: number;
    formatted_address: string;
  } | null>(null);
  const [address, setAddress] = useState({
    street_address: "",
    apartment: "",
    city: "",
    state: "",
    borough: "",
    zip_code: "",
    phone_number: "",
  });
  const [phoneError, setPhoneError] = useState("");
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);

  // Phone verification
  const phoneVerification = usePhoneVerification({
    context: "delivery_address",
    userId: user?.id,
  });

  console.log("[DELIVERY-ADDRESS] Saved addresses:", savedAddresses);

  useEffect(() => {
    if (user) {
      loadSavedAddresses();
    }
  }, [user]);

  const loadSavedAddresses = async () => {
    try {
      const { data, error } = await supabase
        .from("delivery_addresses")
        .select("*")
        .eq("user_id", user?.id)
        .order("is_default", { ascending: false });

      if (error) throw error;
      setSavedAddresses(data || []);
    } catch (error) {
      console.error("Error loading addresses:", error);
    }
  };

  // Geocode the current address
  const handleGeocodeAddress = async () => {
    if (!address.street_address || !address.city || !address.state) {
      platformAlert(
        "Error",
        "Please fill in at least street address, city, and state before verifying location"
      );
      return;
    }

    setIsGeocoding(true);
    setGeocodeResult(null);

    try {
      const addressString = GeocodingService.buildAddressString({
        street_address: address.street_address,
        apartment: address.apartment,
        city: address.city,
        state: address.state,
        zip_code: address.zip_code,
        country: "US",
      });

      console.log("[DELIVERY-ADDRESS] Geocoding address:", addressString);
      const result = await geocodeAddress(addressString);

      if (result) {
        setGeocodeResult(result);
        console.log("[DELIVERY-ADDRESS] Geocoding successful:", result);
        platformAlert(
          "Address Verified",
          `Location found: ${
            result.formatted_address
          }\n\nCoordinates: ${result.latitude.toFixed(
            6
          )}, ${result.longitude.toFixed(6)}`,
          [{ text: "OK" }]
        );
      } else {
        platformAlert(
          "Location Not Found",
          "Could not find the location for this address. Please check the address and try again, or save without verification."
        );
      }
    } catch (error) {
      console.error("[DELIVERY-ADDRESS] Error geocoding:", error);
      platformAlert(
        "Error",
        "Error verifying address location. Please try again."
      );
    } finally {
      setIsGeocoding(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    const requiredFields = [
      "street_address",
      "city",
      "state",
      "zip_code",
      "phone_number",
    ];

    const missingFields = requiredFields.filter((field) => !address[field]);
    if (missingFields.length > 0) {
      // Convert field names to user-friendly labels
      const fieldLabels: Record<string, string> = {
        street_address: "street address",
        city: "city",
        state: "state",
        zip_code: "ZIP code",
        phone_number: "phone number",
      };
      const missingLabels = missingFields.map((f) => fieldLabels[f] || f);
      platformAlert(
        "Error",
        `Please fill in all required fields: ${missingLabels.join(", ")}`
      );
      return;
    }

    // Additional validation
    if (address.zip_code.length < 5) {
      platformAlert("Error", "ZIP code must be at least 5 digits");
      return;
    }

    if (address.state.length !== 2) {
      platformAlert("Error", "State must be a 2-letter code (e.g., NY, CA)");
      return;
    }

    // Validate phone number
    const phoneValidation = validatePhoneNumberWithError(address.phone_number);
    if (!phoneValidation.isValid) {
      setPhoneError(phoneValidation.error || "Invalid phone number");
      platformAlert(
        "Error",
        phoneValidation.error || "Please enter a valid phone number"
      );
      return;
    }

    // Require phone verification
    if (!phoneVerification.isVerified) {
      platformAlert(
        "Phone Verification Required",
        "Please verify your phone number before saving. This ensures you can receive delivery updates.",
        [{ text: "OK" }]
      );
      return;
    }

    // Block saving if address is not verified
    if (!geocodeResult) {
      platformAlert(
        "Address Verification Required",
        "Please verify your address location before saving. This ensures accurate delivery range calculations.",
        [
          {
            text: "Cancel",
            style: "cancel",
          },
          {
            text: "Verify Address",
            onPress: handleGeocodeAddress,
          },
        ]
      );
      return;
    }

    // Check for duplicate address
    const isDuplicate = savedAddresses.some(
      (savedAddr) =>
        savedAddr.street_address.toLowerCase() ===
          address.street_address.toLowerCase() &&
        savedAddr.city.toLowerCase() === address.city.toLowerCase() &&
        savedAddr.state.toLowerCase() === address.state.toLowerCase() &&
        savedAddr.zip_code === address.zip_code &&
        (savedAddr.apartment || "").toLowerCase() ===
          (address.apartment || "").toLowerCase()
    );

    if (isDuplicate) {
      platformAlert(
        "Duplicate Address",
        "This address already exists in your saved addresses. Please use a different address or update the existing one.",
        [{ text: "OK" }]
      );
      return;
    }

    // Clear any previous phone error
    setPhoneError("");

    setLoading(true);
    try {
      // Since verification is required, we already have coordinates
      const coordinates = {
        latitude: geocodeResult.latitude,
        longitude: geocodeResult.longitude,
      };

      console.log(
        "[DELIVERY-ADDRESS] Saving verified address with coordinates:",
        coordinates
      );

      const newAddress = {
        user_id: user.id,
        street_address: address.street_address,
        apartment: address.apartment || null,
        city: address.city,
        state: address.state.toUpperCase(),
        borough: address.borough ?? null,
        zip_code: address.zip_code,
        phone_number: formatPhoneNumberForAPI(address.phone_number),
        delivery_instructions: null,
        is_default: true, // Always set new address as default
        updated_at: new Date().toISOString(),
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
      };

      const { data, error } = await supabase
        .from("delivery_addresses")
        .insert([newAddress])
        .select()
        .single();

      if (error) throw error;

      // Set all other addresses to not default
      await supabase
        .from("delivery_addresses")
        .update({ is_default: false })
        .neq("id", data.id)
        .eq("user_id", user?.id);

      // Save address data to user profile
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          street_address: address.street_address,
          apartment: address.apartment || null,
          city: address.city,
          state: address.state.toUpperCase(),
          borough: null,
          zip_code: address.zip_code,
          phone_number: formatPhoneNumberForAPI(address.phone_number),
          delivery_instructions: null,
          coordinates: {
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
          },
        })
        .eq("id", user.id);

      if (profileError) {
        console.error("Error updating profile with address:", profileError);
        // Don't fail the entire operation if profile update fails
      }

      // Update saved addresses list
      await loadSavedAddresses();

      // Always update context with the new address (now the default)
      await updateAddress(data);

      platformAlert(
        "Success",
        "Address saved successfully with verified location"
      );
      setAddress({
        street_address: "",
        apartment: "",
        city: "",
        state: "",
        borough: "",
        zip_code: "",
        phone_number: "",
      });
      setPhoneError("");
      setGeocodeResult(null);
      phoneVerification.reset();
    } catch (error) {
      console.error("Error saving address:", error);

      let errorMessage = "Failed to save address";

      if (error?.message) {
        // Check for common database errors
        if (
          error.message.includes("duplicate key") ||
          error.message.includes("unique constraint")
        ) {
          errorMessage = "This address already exists";
        } else if (error.message.includes("foreign key constraint")) {
          errorMessage =
            "User authentication error. Please try signing out and back in.";
        } else if (error.message.includes("null value in column")) {
          errorMessage = "Please fill in all required fields";
        } else if (error.message.includes("invalid input syntax")) {
          errorMessage = "Invalid data format. Please check your entries.";
        } else {
          // Use the original error message for debugging
          errorMessage = `Failed to save address: ${error.message}`;
        }
      }

      platformAlert("Error", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleStateChange = (text: string) => {
    // Convert to uppercase and limit to 2 characters
    const formattedState = text.toUpperCase().slice(0, 2);
    setAddress((prev) => ({
      ...prev,
      state: formattedState,
    }));
    // Reset geocode result when address changes
    if (geocodeResult) {
      setGeocodeResult(null);
    }
  };

  const handleAddressFieldChange = (field: string, value: string) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
    // Reset geocode result when address changes
    if (geocodeResult) {
      setGeocodeResult(null);
    }
  };

  const handlePhoneChange = (text: string) => {
    // Format the phone number as the user types
    const formattedPhone = formatPhoneNumberAsUserTypes(
      text,
      address.phone_number
    );
    setAddress((prev) => ({ ...prev, phone_number: formattedPhone }));

    // Clear error when user starts typing
    if (phoneError) {
      setPhoneError("");
    }
  };

  const handleSelectAddress = async (selectedAddress: SavedAddress) => {
    try {
      // Update is_default in database
      const { error } = await supabase
        .from("delivery_addresses")
        .update({ is_default: true })
        .eq("id", selectedAddress.id);

      // Set all other addresses to not default
      await supabase
        .from("delivery_addresses")
        .update({ is_default: false })
        .neq("id", selectedAddress.id)
        .eq("user_id", user?.id);

      if (error) throw error;

      // Update profiles table with selected address data
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          street_address: selectedAddress.street_address,
          apartment: selectedAddress.apartment,
          city: selectedAddress.city,
          state: selectedAddress.state,
          borough: selectedAddress.borough,
          zip_code: selectedAddress.zip_code,
          phone_number: selectedAddress.phone_number,
          delivery_instructions: selectedAddress.delivery_instructions,
          coordinates: {
            latitude: selectedAddress.latitude,
            longitude: selectedAddress.longitude,
          },
        })
        .eq("id", user?.id);

      if (profileError) {
        console.error("Error updating profile with address:", profileError);
        // Don't fail the entire operation if profile update fails
      }

      // Update context
      await updateAddress(selectedAddress);

      // Refresh list
      await loadSavedAddresses();

      platformAlert("Success", "Default delivery address updated");
    } catch (error) {
      console.error("Error updating default address:", error);
      platformAlert("Error", "Failed to update default address");
    }
  };

  const handleDeleteAddress = async (addressToDelete: SavedAddress) => {
    try {
      const { error } = await supabase
        .from("delivery_addresses")
        .delete()
        .eq("id", addressToDelete.id)
        .eq("user_id", user?.id); // Extra safety check

      if (error) throw error;

      // Refresh the addresses list
      await loadSavedAddresses();

      platformAlert("Success", "Address removed successfully");
    } catch (error) {
      console.error("Error deleting address:", error);
      platformAlert("Error", "Failed to remove address");
    }
  };

  return (
    <SafeAreaView edges={["bottom"]} style={styles.safeArea}>
      <KeyboardAwareScrollView style={styles.container} extraHeight={100}>
        <SavedAddressList
          addresses={savedAddresses}
          onSelect={handleSelectAddress}
          onDelete={handleDeleteAddress}
          selectedId={selectedAddress?.id}
        />

        <View style={styles.formSection}>
          <Text style={styles.title}>Add Address</Text>
          <Input
            label="Street Address"
            value={address.street_address}
            onChangeText={(text) =>
              handleAddressFieldChange("street_address", text)
            }
            placeholder="Enter your street address"
          />

          <Input
            label="Apartment/Unit (Optional)"
            value={address.apartment}
            onChangeText={(text) => handleAddressFieldChange("apartment", text)}
            placeholder="Apt, Suite, Unit, etc."
          />

          <Input
            label="City"
            value={address.city}
            onChangeText={(text) => handleAddressFieldChange("city", text)}
            placeholder="Enter city"
          />

          <Input
            label="State"
            value={address.state}
            onChangeText={handleStateChange}
            placeholder="Enter state (e.g., NY)"
            maxLength={2}
            autoCapitalize="characters"
          />

          <Input
            label="ZIP Code"
            value={address.zip_code}
            onChangeText={(text) => handleAddressFieldChange("zip_code", text)}
            placeholder="Enter ZIP code"
            keyboardType="numeric"
            maxLength={5}
          />

          <PhoneVerification
            phoneNumber={address.phone_number}
            onPhoneChange={(phone) => {
              setAddress((prev) => ({ ...prev, phone_number: phone }));
              if (phoneError) setPhoneError("");
            }}
            isVerified={phoneVerification.isVerified}
            codeSent={phoneVerification.codeSent}
            isSending={phoneVerification.isSending}
            isVerifying={phoneVerification.isVerifying}
            error={phoneVerification.error}
            onSendCode={() => phoneVerification.sendVerificationCode(address.phone_number)}
            onVerifyCode={(code) => phoneVerification.verifyCode(address.phone_number, code)}
            onReset={phoneVerification.reset}
            label="Phone Number"
          />

          {/* Geocoding Section */}
          {address.street_address && address.city && address.state && (
            <View style={styles.geocodingSection}>
              {!geocodeResult && (
                <View style={styles.verificationRequired}>
                  <Feather
                    name="alert-circle"
                    size={16}
                    color={colors.warning || colors.primary}
                  />
                  <Text style={styles.verificationRequiredText}>
                    Address verification is required to save
                  </Text>
                </View>
              )}

              <Button
                onPress={handleGeocodeAddress}
                loading={isGeocoding}
                style={styles.geocodeButton}
                variant={geocodeResult ? "primary" : "outline"}
              >
                <View style={styles.geocodeButtonContent}>
                  <Feather
                    name={geocodeResult ? "check-circle" : "map-pin"}
                    size={16}
                    color={geocodeResult ? "#000" : colors.primary}
                  />
                  <Text
                    style={[
                      styles.geocodeButtonText,
                      { color: geocodeResult ? "#000" : colors.primary },
                    ]}
                  >
                    {isGeocoding
                      ? "Verifying Location..."
                      : geocodeResult
                      ? "Location Verified"
                      : "Verify Address Location"}
                  </Text>
                </View>
              </Button>

              {geocodeResult && (
                <View style={styles.geocodeResult}>
                  <View style={styles.geocodeResultHeader}>
                    <Feather
                      name="check-circle"
                      size={16}
                      color={colors.success}
                    />
                    <Text style={styles.geocodeResultTitle}>
                      Address Location Verified
                    </Text>
                  </View>
                  <Text style={styles.geocodeResultAddress}>
                    {geocodeResult.formatted_address}
                  </Text>
                  <Text style={styles.geocodeResultCoords}>
                    Coordinates: {geocodeResult.latitude.toFixed(6)},{" "}
                    {geocodeResult.longitude.toFixed(6)}
                  </Text>
                </View>
              )}
            </View>
          )}

          <Button
            onPress={handleSave}
            loading={loading}
            style={styles.button}
            disabled={
              !geocodeResult &&
              !!(address.street_address && address.city && address.state)
            }
          >
            {!geocodeResult &&
            address.street_address &&
            address.city &&
            address.state
              ? "Verify Address to Save"
              : "Save Address"}
          </Button>
        </View>
      </KeyboardAwareScrollView>
    </SafeAreaView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    safeArea: {
      flex: 1,
      backgroundColor: colors.background,
    },
    container: {
      flex: 1,
      backgroundColor: colors.background,
      padding: spacing.lg,
      gap: spacing.md,
      paddingBottom: spacing.xl,
    },
    scroll: {
      paddingBottom: spacing.xl,
    },
    button: {
      marginBottom: spacing.lg,
    },
    formSection: {
      gap: spacing.md,
    },
    title: {
      fontSize: 16,
      fontWeight: "600",
      marginBottom: spacing.sm,
      color: colors.text,
    },
    geocodingSection: {
      gap: spacing.sm,
      marginTop: spacing.sm,
    },
    geocodeButton: {
      marginBottom: 0,
    },
    geocodeButtonContent: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.xs,
    },
    geocodeButtonText: {
      color: colors.primary,
      fontSize: 16,
      fontWeight: "600",
    },
    geocodeResult: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: spacing.md,
      gap: spacing.xs,
      borderWidth: 1,
      borderColor: colors.success,
    },
    geocodeResultHeader: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.xs,
    },
    geocodeResultTitle: {
      color: colors.success,
      fontSize: 16,
      fontWeight: "600",
    },
    geocodeResultAddress: {
      fontSize: 14,
      color: colors.text,
      fontStyle: "italic",
      marginTop: spacing.xs,
    },
    geocodeResultCoords: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    verificationRequired: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.xs,
      paddingVertical: spacing.xs,
    },
    verificationRequiredText: {
      fontSize: 14,
      color: colors.warning || colors.primary,
      fontWeight: "500",
    },
  });
